<script setup lang="ts">
import { createReusableTemplate, useIntervalFn } from '@vueuse/core';
import { computed, ref, shallowRef, useTemplateRef, watch } from 'vue';

import type { FiscalShiftAction } from '@/entities/fiscalization';

import { OrganizationModule, canApproveRefunds, useAuthStore } from '@/entities/auth';
import { useKassaLocationMarker } from '@/entities/fiscalization';
import { LocationCodeBadge, locationColorStyle, organizationCode, useLocationAttention } from '@/entities/location';
import {
  OrderFiscalShiftModals,
  useFiscalShiftPresentation,
  useFiscalShiftStatus,
} from '@/features/order-fiscalization';
import { useSession } from '@/features/session';
import { BaseDropdown, STATUS_DOT_CLASSES } from '@/shared/ui';
import { IconChevronRight } from '@/shared/ui/icons';
import { BaseSidePanel } from '@/shared/ui/side-panel';
import { clock } from '@shared/lib/date-time';

import type { PlaqueTone } from '../lib/location-plaque-state';

import { buildPlaqueAttention, plaqueDotTone } from '../lib/location-plaque-state';
import LocationPlaquePanel from './LocationPlaquePanel.vue';
import LocationShiftHint from './LocationShiftHint.vue';

/**
 * Плашка «где я работаю»: код и цвет текущей локации, точка смены и число «Нужно решить».
 * Единственный владелец состояния смены на странице: модалки смены живут здесь одним экземпляром.
 */
const props = defineProps<{ variant: 'header' | 'rail' }>();

const auth = useAuthStore();
const location = computed(() => auth.currentLocation);
const faceStyle = computed(() => locationColorStyle(location.value?.color));
const locationId = computed(() => location.value?.id ?? '');
const canSeeShift = computed(() => auth.hasPermission('payments.accept') && auth.hasModule(OrganizationModule.Orders));
const status = useFiscalShiftStatus({
  locationId,
  enabled: () => canSeeShift.value && locationId.value !== '',
  backgroundRefresh: 'poll',
});
const presentation = useFiscalShiftPresentation(status, locationId);
const { byLocation } = useLocationAttention(() => auth.currentOrganization !== null);
const marker = useKassaLocationMarker(() => auth.currentOrganization?.id);
const kassaLocationId = shallowRef(marker.locationId());
const now = shallowRef(clock.now());
const { logout } = useSession();

// Закрывший смену уходит: следующий сотрудник не застанет чужую учётку на общем устройстве.
const onShiftClosed = (): void => {
  if (auth.isImpersonating) {
    console.warn('[shift.close] logout skipped: impersonation');
    return;
  }
  void logout();
};
useIntervalFn(() => {
  now.value = clock.now();
}, 60_000);

// Отметку ставит только загруженный статус кассы: до ответа «не касса» — незнание, а не факт.
watch(
  [status.isWorkstationBound, status.operationalStatus, locationId],
  ([bound, operational, id]) => {
    if (operational === null || id === '') return;
    if (bound) marker.mark(id);
    else marker.clear(id);
    kassaLocationId.value = marker.locationId();
  },
  { immediate: true },
);

const dotTone = computed(() =>
  plaqueDotTone({
    shiftState: status.shiftState.value,
    kktConcern: status.kktConcern.value,
    reminderStage: presentation.reminder.value?.stage ?? null,
    isWorkstationBound: status.isWorkstationBound.value,
  }),
);
const attention = computed(() =>
  buildPlaqueAttention({
    currentLocationId: location.value?.id ?? null,
    locations: auth.availableLocations.map(({ id, title, timezone }) => ({ id, title, timezone })),
    attention: byLocation.value,
    local: {
      shiftState: status.shiftState.value,
      kktConcern: status.kktConcern.value,
      reminder: presentation.reminder.value,
      isWorkstationBound: status.isWorkstationBound.value,
    },
    canApproveRefunds: canApproveRefunds(auth),
    now: now.value,
  }),
);

const code = computed(() => location.value?.short_name ?? organizationCode(auth.currentOrganization?.title ?? ''));
const DOT_CLASSES: Record<PlaqueTone, string> = {
  danger: STATUS_DOT_CLASSES.error,
  success: STATUS_DOT_CLASSES.success,
  warning: STATUS_DOT_CLASSES.warning,
};
const ariaLabel = computed(() =>
  [
    `Локация ${location.value?.title ?? auth.currentOrganization?.title ?? ''}`,
    canSeeShift.value ? presentation.visiblePresentation.value.title : '',
    attention.value.total > 0 ? `Нужно решить: ${attention.value.total}` : '',
  ]
    .filter(Boolean)
    .join('. '),
);
const badgeCount = computed(() => (attention.value.total > 99 ? '99+' : String(attention.value.total)));

/** Код, точка смены и счётчик одинаковы в боковой панели и шапке. */
const [DefinePlaqueFace, PlaqueFace] = createReusableTemplate<{ size: 'lg' | 'md' }>();

/** Закрытая смена текущей локации — повод напомнить у плашки рейла. */
const closedShiftLocationId = computed(() =>
  canSeeShift.value && status.shiftState.value?.status === 'shift_closed' ? locationId.value || null : null,
);
const plaqueButton = useTemplateRef<HTMLElement>('plaqueButton');
const shiftHint = useTemplateRef<{ dismiss: () => void }>('shiftHint');

function onPlaqueClick(toggle: () => void): void {
  shiftHint.value?.dismiss();
  toggle();
}

const modals = ref<{ beginAction: (action: Exclude<FiscalShiftAction, null>) => Promise<void> } | null>(null);
const sheetOpen = ref(false);

function onShiftAction(action: Exclude<FiscalShiftAction, null>): void {
  void modals.value?.beginAction(action);
}
</script>

<template>
  <DefinePlaqueFace v-slot="{ size }">
    <span
      class="location-plaque__face"
      :style="faceStyle"
    >
      <LocationCodeBadge
        :code="code"
        :color="location?.color ?? null"
        :size="size"
        class="location-plaque__code"
        solid
      />
      <span
        v-if="dotTone"
        class="location-plaque__dot"
        :class="DOT_CLASSES[dotTone]"
        aria-hidden="true"
      />
      <span
        v-if="attention.total > 0"
        class="location-plaque__count"
        :class="attention.tone === 'danger' ? 'bg-(--danger)' : 'bg-(--warning) text-(--on-warning)'"
        aria-hidden="true"
      >
        {{ badgeCount }}
      </span>
    </span>
  </DefinePlaqueFace>

  <BaseDropdown
    v-if="props.variant === 'rail'"
    width="20rem"
    :content="{ side: 'right', align: 'start', sideOffset: 8 }"
  >
    <template #trigger="{ toggle, isOpen }">
      <button
        ref="plaqueButton"
        type="button"
        class="location-plaque"
        :aria-label="ariaLabel"
        :aria-expanded="isOpen"
        aria-haspopup="dialog"
        @click="onPlaqueClick(toggle)"
      >
        <PlaqueFace size="lg" />
        <IconChevronRight
          class="location-plaque__chevron"
          :width="6"
          :height="10"
          aria-hidden="true"
        />
      </button>
    </template>
    <template #content="{ close }">
      <LocationPlaquePanel
        :status="status"
        :can-see-shift="canSeeShift"
        :groups="attention.groups"
        :by-location="byLocation"
        :now="now"
        :kassa-location-id="kassaLocationId"
        @close="close"
        @shift-action="onShiftAction"
      />
    </template>
  </BaseDropdown>

  <template v-else>
    <button
      type="button"
      class="location-plaque location-plaque--header"
      :aria-label="ariaLabel"
      :aria-expanded="sheetOpen"
      aria-haspopup="dialog"
      @click="sheetOpen = true"
    >
      <PlaqueFace size="md" />
    </button>
    <BaseSidePanel
      :open="sheetOpen"
      side="bottom"
      label="Локация"
      @close="sheetOpen = false"
    >
      <LocationPlaquePanel
        :status="status"
        :can-see-shift="canSeeShift"
        :groups="attention.groups"
        :by-location="byLocation"
        :now="now"
        :kassa-location-id="kassaLocationId"
        @close="sheetOpen = false"
        @shift-action="onShiftAction"
      />
    </BaseSidePanel>
  </template>

  <LocationShiftHint
    v-if="props.variant === 'rail'"
    ref="shiftHint"
    :anchor="plaqueButton"
    :location-id="closedShiftLocationId"
    @open-shift="onShiftAction('open')"
  />

  <OrderFiscalShiftModals
    v-if="canSeeShift && location"
    ref="modals"
    :status="status"
    @closed="onShiftClosed"
  />
</template>

<style scoped>
@reference "../../../tailwind.css";

.location-plaque {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 44px;
  min-height: 44px;
  border-radius: 0.75rem;
  cursor: pointer;
  transition: transform 150ms ease-out;
}

/* Точка и счётчик держатся за углы самой плашки, а не кнопки: в рейле в кнопке ещё и шеврон. */
.location-plaque__face {
  position: relative;
  display: inline-flex;
}

.location-plaque__code {
  transition: box-shadow 150ms ease-out;
}

/* Шеврон справа, туда откроется панель; плашка с шевроном центрируются в рейле вместе. */
.location-plaque__chevron {
  margin-left: 4px;
  color: var(--text-secondary);
  opacity: 0.6;
  transition:
    opacity 150ms ease-out,
    transform 150ms ease-out;
}

/* Плашка сплошная, серый квадрат пунктов рейла на ней не виден: наведение и открытая панель
   обводят её кольцом цвета локации и подтягивают шеврон. */
.location-plaque:not(.location-plaque--header)[aria-expanded='true'] .location-plaque__code {
  box-shadow:
    0 0 0 2px var(--sidebar-rail-bg),
    0 0 0 4px var(--location-color);
}

.location-plaque[aria-expanded='true'] .location-plaque__chevron {
  opacity: 1;
  transform: translateX(2px);
}

@media (hover: hover) {
  .location-plaque:not(.location-plaque--header):hover .location-plaque__code {
    box-shadow:
      0 0 0 2px var(--sidebar-rail-bg),
      0 0 0 4px var(--location-color);
  }

  .location-plaque:hover .location-plaque__chevron {
    opacity: 1;
    transform: translateX(2px);
  }
}

@media (prefers-reduced-motion: reduce) {
  .location-plaque,
  .location-plaque__code,
  .location-plaque__chevron {
    transition: none;
  }
}

.location-plaque:active {
  transform: scale(0.96);
}

.location-plaque:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
}

/* Точка смены снизу справа, обведена цветом фона рейла. */
.location-plaque__dot {
  position: absolute;
  right: -3px;
  bottom: -3px;
  width: 12px;
  height: 12px;
  border-radius: 999px;
  box-shadow: 0 0 0 2px var(--sidebar-rail-bg);
}

/* Число «Нужно решить» — как бейдж пункта рейла. */
.location-plaque__count {
  position: absolute;
  top: -6px;
  right: -6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 999px;
  color: var(--text-primary);
  @apply text-2xs;
  font-weight: 600;
  line-height: 1;
  font-variant-numeric: tabular-nums;
}

/* В шапке рядом стоит дата: точка и счётчик внутри плашки, заливка на всём их ряду. */
.location-plaque--header .location-plaque__face {
  align-items: center;
  gap: 4px;
  height: 28px;
  padding-inline: 6px;
  border-radius: 0.375rem;
  /* Та же заливка, что у сплошного `LocationCodeBadge`: белый текст светлой темы держит контраст. */
  background: color-mix(in srgb, var(--location-color) var(--location-solid-depth), var(--text-primary));
}

.location-plaque--header .location-plaque__code {
  height: auto;
  min-width: 0;
  padding: 0;
  background: transparent;
}

.location-plaque--header .location-plaque__dot {
  position: static;
  order: -1;
  width: 8px;
  height: 8px;
  box-shadow: 0 0 0 1.5px var(--surface-page);
}

.location-plaque--header .location-plaque__count {
  position: static;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
}
</style>
